
import { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
/** Legacy component name: manages CompanyGroupAdmin rows (Companies menu). */
import { getCompanyGroupAdmins } from '../core/_requests'
import { CompanyAdmin } from '../core/_models'
import AddCompanyAdminModal from './AddCompanyAdminModal'
import DeleteCompanyAdminModal from './DeleteCompanyAdminModal'
import EditCompanyAdminModal from './EditCompanyAdminModal'
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { assignReport, getAssignedTemplatesByCompanyAdmin, getTemplateList } from '../../operations/core/_requests'
import ViewCompanyAdminModal from './ViewCompanyAdminModal'
import * as XLSX from 'xlsx';
import { useAuth } from '../../auth'
import { companyGroupAdminGates } from '../core/adminPermissions'
import {
    adminListEmptyMessage,
    initialAdminListAccessState,
    resolveAdminListFetchError,
    type AdminListAccessState,
} from '../core/adminListAccess'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
const app_link = process.env.REACT_APP_URL;

const CompanyAdminList: FC = () => {
    const { currentUser, hasPermission, isSuperAdmin } = useAuth()
    const { state: globalState } = useGlobalFilter()
    const actionGates = useMemo(
        () => companyGroupAdminGates(hasPermission, isSuperAdmin),
        [hasPermission, isSuperAdmin]
    )
    const canExportCompanies = isSuperAdmin || hasPermission('DOWNLOAD_COMPANY_GROUP_ADMIN')
    const [listAccess, setListAccess] = useState<AdminListAccessState>(initialAdminListAccessState)
    const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState<boolean>(false);
    const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState<boolean>(false);
    const [isViewCompanyModalOpen, setIsViewCompanyModalOpen] = useState<boolean>(false);
    const [isDeleteCompanyModalOpen, setIsDeleteCompanyModalOpen] = useState<boolean>(false);
    const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<CompanyAdmin>();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const indexOfLastCompanyAdmin = currentPage * rowsPerPage;
    const indexOfFirstCompanyAdmin = indexOfLastCompanyAdmin - rowsPerPage;
    const [filteredCompanies, setFilteredCompanies] = useState<CompanyAdmin[]>([]);
    const totalPages = Math.ceil(filteredCompanies.length / rowsPerPage);
    const [loginDetails, setLoginDetails] = useState<Record<number, { username: string; password: string }>>({});
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [newCompanyCredentials, setNewCompanyCredentials] = useState<{ username: string; password: string } | null>(null);
    const [sortConfig, setSortConfig] = useState<{ key: keyof CompanyAdmin | 'contact' | null, direction: 'asc' | 'desc' }>({
        key: null,
        direction: 'asc',
    });
    const handleSort = (key: keyof CompanyAdmin) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            } else {
                return { key, direction: 'asc' };
            }
        });
    };



    useEffect(() => {
        fetchCompanyAdmins();
    }, []);

    useEffect(() => {
        const refresh = () => { void fetchCompanyAdmins() }
        window.addEventListener('rbac-permissions-updated', refresh)
        return () => window.removeEventListener('rbac-permissions-updated', refresh)
    }, []);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [filteredCompanies, rowsPerPage]);

    useEffect(() => {
        const stored = localStorage.getItem('companyLoginDetails');
        if (stored) setLoginDetails(JSON.parse(stored));
    }, []);

    const handleExcelDownload = () => {
        try {
            if (!filteredCompanies || filteredCompanies.length === 0) {
                toast.warn('No company data available to export.')
                return
            }

            const exportData = filteredCompanies.map((c) => ({
                'Company Name': c.name || '',
                'Contact': c.contactNo || '',
                'Alt Contact': c.altContactNo || '',
                'Email': c.email || '',
                'Address': [c.addressLine1, c.addressLine2].filter(Boolean).join(', ') || '',
                'City': c.city || '',
                'State': c.state || '',
                'Country': c.country || '',
                'Currency': c.primaryCurrencyCode || '',
                'GST Number': c.gstNumber || '',
                'PAN Number': c.panNumber || '',
                'VAT Number': c.vatNumber || '',
                'Status': c.active ? 'Active' : 'Inactive',
            }))

            const worksheet = XLSX.utils.json_to_sheet(exportData)
            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Companies')
            XLSX.writeFile(workbook, 'companies.xlsx')
            toast.success(`Exported ${filteredCompanies.length} companies to Excel.`)
        } catch (err) {
            console.error('Failed to export companies to Excel:', err)
            showErrorToast(err, 'Failed to export companies to Excel.')
        }
    };

    const fetchCompanyAdmins = async () => {
        if (!actionGates.canRead) {
            setListAccess({ loading: false, accessDenied: true, loadError: null })
            setCompanyAdmins([])
            return
        }
        setListAccess((s) => ({ ...s, loading: true, accessDenied: false, loadError: null }))
        try {
            const companyList = await getCompanyGroupAdmins();
            setCompanyAdmins(Array.isArray(companyList) ? companyList : []);
            setListAccess({ loading: false, accessDenied: false, loadError: null })
        } catch (error) {
            console.error('Failed to fetch company list:', error);
            setCompanyAdmins([])
            const nextAccess = resolveAdminListFetchError(error, 'Failed to load companies')
            setListAccess(nextAccess)
            showErrorToast(error, 'Failed to load companies', 'load')
        }
    };

    const handleCompanyAdded = async () => {
        fetchCompanyAdmins();

        try {
            // Step 1: Fetch all company admins
            const companyList = await getCompanyGroupAdmins();
            setCompanyAdmins(companyList);

            // Step 2: Get the latest added company admin (assuming it's last in the list)
            const newCompanyAdmin = companyList[companyList.length - 1];
            const companyAdminId = newCompanyAdmin?.id;

            if (!companyAdminId) {
                console.error("New company admin not found.");
                return;
            }

            // Step 3: Fetch assigned templates for the new company admin
            const assignedTemplates = await getAssignedTemplatesByCompanyAdmin(companyAdminId);


            // Step 4: If no templates are assigned, fetch base templates and assign all
            if (!assignedTemplates || assignedTemplates.length === 0) {
                const baseTemplateIds = await fetchTemplateIds();

                if (!baseTemplateIds || baseTemplateIds.length === 0) {
                    return;  // ✅ Don't assign anything if no base templates
                }

                for (const templateId of baseTemplateIds) {
                    await assignReport(templateId, { companyAdminId } as any); // ✅ Correct
                }

                console.log("Reports assigned to new company admin.");
            }

        } catch (error) {
            console.error('Error in handleCompanyAdded:', error);
            console.log("Failed to assign base templates.");
        }
    };

    const fetchTemplateIds = async () => {
        try {
            const templateList = await getTemplateList();

            // Extract all IDs
            const templateIds: number[] = templateList
                .map(template => template.id);

            return templateIds;
        } catch (err) {
            console.error('Failed to fetch template list:', err);
        }
    };

    useEffect(() => {
        let filtered = (companyAdmins ?? []).filter(company => {
            const matchesSearch = company.name?.toLowerCase().includes(searchTerm.toLowerCase())

            // Global filter: company
            let matchesCompany = true
            const gc = globalState.company
            if (gc && gc.type === 'company_group') {
                matchesCompany = company.id === gc.id
            }

            return matchesSearch && matchesCompany
        });

        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                const getSortValue = (company: CompanyAdmin) => {
                    switch (sortConfig.key) {
                        case 'name': return company.name?.trim().toLowerCase() || '';
                        case 'email': return company.email?.trim().toLowerCase() || '';
                        case 'contact': return company.contactNo?.trim().toLowerCase() || '';
                        case 'city': return company.city?.trim().toLowerCase() || '';
                        case 'state': return company.state?.trim().toLowerCase() || '';
                        case 'country': return company.country?.trim().toLowerCase() || '';
                        default:
                            return (company[sortConfig.key!] as string)?.trim().toLowerCase() || '';
                    }
                };

                const aValue = getSortValue(a);
                const bValue = getSortValue(b);

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        setFilteredCompanies(filtered);
        setCurrentPage(1);
    }, [searchTerm, companyAdmins, sortConfig, globalState.company]);

    return (
        <div
            className='app-main flex-column flex-row-fluid'
            id='kt_app_main'
        >
            <div className='d-flex flex-column flex-column-fluid' >
                <div
                    id='kt_app_content'
                    className='app-content flex-column-fluid d-flex flex-column'
                    style={{ flex: 1 }}
                >
                    <div className='card flex-column-fluid d-flex flex-column bg-white border-top' style={{ flex: 1, }}>
                        {/* Header */}
                        <div className='d-flex gap-3 pe-5'>
                            <div className='py-3 ms-3' style={{ flex: 5 }}>
                                <div className='d-flex align-items-center gap-3 mb-5'>
                                    <h3 className='card-title fw-bold text-dark mb-0'>Companies</h3>
                                    {listAccess.loading && (
                                        <span className='badge badge-light-primary d-inline-flex align-items-center gap-2 py-2 px-3 fs-7 fw-normal'>
                                            <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true'></span>
                                            Loading companies...
                                        </span>
                                    )}
                                </div>
                                <div className='d-flex justify-content-between mb-5'>
                                    <div className='d-flex align-items-center gap-2'>
                                        <input
                                            type="text"
                                            className="form-control cp_search_input"
                                            placeholder="Company Name"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className='d-flex align-items-center gap-3'>
                                        {canExportCompanies && (
                                            <button
                                                className='btn text-white d-inline-flex align-items-center justify-content-center'
                                                style={{
                                                    backgroundColor: '#334155',
                                                    borderRadius: '9.75px',
                                                    padding: '8px 13px',
                                                    fontSize: '11.5px',
                                                    fontWeight: 700,
                                                    gap: '6.5px',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                                    transition: 'all 0.2s ease',
                                                    border: 'none',
                                                    height: '36px'
                                                }}
                                                onClick={handleExcelDownload}
                                                title="Export to Excel"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                                Export Excel
                                            </button>
                                        )}
                                        {currentUser?.role.roleName == 'SUPER_ADMIN' && (
                                            <button
                                                className='btn btn-primary text-white d-inline-flex align-items-center justify-content-center'
                                                style={{
                                                    borderRadius: '9.75px',
                                                    padding: '8px 13px',
                                                    fontSize: '11.5px',
                                                    fontWeight: 700,
                                                    gap: '6.5px',
                                                    boxShadow: '0 2px 4px rgba(37,99,235,0.2)',
                                                    transition: 'all 0.2s ease',
                                                    border: 'none',
                                                    height: '36px'
                                                }}
                                                onClick={() => { setIsAddCompanyModalOpen(true) }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="12" y1="5" x2="12" y2="19" />
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                </svg>
                                                Add Company
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Notice / Error Banner */}
                                {listAccess.loadError && (
                                    <div className='alert alert-danger d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 border border-danger-subtle'>
                                        <div className='d-flex align-items-center gap-3'>
                                            <i className='bi bi-exclamation-triangle-fill fs-3 text-danger' />
                                            <div>
                                                <div className='fw-bold text-danger fs-7'>Failed to Load Companies</div>
                                                <div className='text-dark fs-8'>{listAccess.loadError}</div>
                                            </div>
                                        </div>
                                        <button
                                            className='btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-3 py-1 text-nowrap'
                                            onClick={fetchCompanyAdmins}
                                            disabled={listAccess.loading}
                                        >
                                            <i className={`bi bi-arrow-clockwise ${listAccess.loading ? 'spinner-border spinner-border-sm' : ''}`} />
                                            {listAccess.loading ? 'Retrying...' : 'Retry'}
                                        </button>
                                    </div>
                                )}
                                {listAccess.accessDenied && (
                                    <div className='alert alert-warning d-flex align-items-center p-3 mb-4 rounded-3 border border-warning-subtle'>
                                        <i className='bi bi-shield-lock-fill fs-3 text-warning me-3' />
                                        <div>
                                            <div className='fw-bold text-warning fs-7'>Access Restricted</div>
                                            <div className='text-muted fs-8'>You do not have permission to view companies. Contact your administrator if you need access.</div>
                                        </div>
                                    </div>
                                )}
                                {/* Summary Cards */}
                                <div className='py-3'>
                                    {/* Reports Table */}
                                    <div className="report-table table-responsive">
                                        <table className="table table-bordered align-middle">
                                            <thead className="table-header py-5">
                                                <tr>
                                                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                        COMPANY NAME
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th onClick={() => handleSort('contactNo')} style={{ cursor: 'pointer' }}>
                                                        CONTACT
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                                                        EMAIL
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th onClick={() => handleSort('state')} style={{ cursor: 'pointer' }}>
                                                        STATE
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>
                                                        CITY
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th onClick={() => handleSort('country')} style={{ cursor: 'pointer' }}>
                                                        COUNTRY
                                                        <KTSVG path="/media/map/report_table_filter.svg" className="svg-icon-small ms-2" />
                                                    </th>
                                                    <th>ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody className="table-body">
                                                {listAccess.loading ? (
                                                    Array.from({ length: 8 }).map((_, i) => (
                                                        <tr key={`company-sk-${i}`}>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-9 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-6 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-6 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                                                                </div>
                                                            </td>
                                                            <td className='py-3'>
                                                                <div className='placeholder-glow'>
                                                                    <span className='placeholder col-8 rounded' style={{ height: 24 }}></span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : filteredCompanies.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={7} className="text-center py-4 text-muted">
                                                            {adminListEmptyMessage(listAccess, 'No companies found')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredCompanies
                                                        .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                                                        .map((company, idx) => {
                                                            return (
                                                                <tr key={idx}>
                                                                    <td>{company.name}</td>
                                                                    <td>{company.contactNo}</td>
                                                                    <td>{company.email}</td>
                                                                    <td>{company.state}</td>
                                                                    <td>{company.city}</td>
                                                                    <td>{company.country}</td>
                                                                    <td>
                                                                        {actionGates.canUpdate && (
                                                                            <button className="btn btn-sm px-0"
                                                                                onClick={() => {
                                                                                    setIsEditCompanyModalOpen(true);
                                                                                    setSelectedCompany(company);
                                                                                }}>
                                                                                <KTSVG path='/media/map/edit-active.svg' className='' />
                                                                            </button>
                                                                        )}
                                                                        {actionGates.canDelete && (
                                                                            <button
                                                                                className="btn btn-sm px-0"
                                                                                onClick={() => {
                                                                                    setIsDeleteCompanyModalOpen(true);
                                                                                    setSelectedCompany(company);
                                                                                }}
                                                                            >
                                                                                <KTSVG path='/media/map/trash.svg' className='' />
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-sm px-0"
                                                                            onClick={() => {
                                                                                setIsViewCompanyModalOpen(true);
                                                                                setSelectedCompany(company);
                                                                            }}
                                                                        >
                                                                            <KTSVG path='/media/map/ph_eye.svg' className='' />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                )}
                                            </tbody>
                                        </table>
                                        {/* Pagination */}
                                        <div className="pagination-wrapper d-flex justify-content-between align-items-center py-3" style={{ borderTop: "1px solid #dee2e6" }}>
                                            <div className="d-flex align-items-center">
                                                <span className="text-muted me-2">Rows per page</span>
                                                <SearchableSelect
                                                    className="form-select form-select-sm"
                                                    style={{
                                                        borderRadius: "20px",
                                                        width: "70px",
                                                        border: "1px solid #dee2e6",
                                                        fontSize: "14px",
                                                        padding: "4px 8px"
                                                    }}
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        setRowsPerPage(parseInt(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </SearchableSelect>
                                            </div>

                                            <div className="d-flex align-items-center">
                                                <span className="text-muted me-3" style={{ fontSize: "14px" }}>
                                                    Showing <strong>{(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, filteredCompanies.length)}</strong> of <strong>{filteredCompanies.length}</strong>
                                                </span>

                                                <nav>
                                                    <ul className="pagination pagination-sm mb-0" style={{ gap: "2px" }}>
                                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                            <button
                                                                className="page-link text-muted"
                                                                style={{
                                                                    backgroundColor: "#f8f9fa",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "8px 12px",
                                                                    fontSize: "14px",
                                                                    borderRadius: "6px"
                                                                }}
                                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                disabled={currentPage === 1}
                                                            >
                                                                ‹
                                                            </button>
                                                        </li>

                                                        {(() => {
                                                            const totalPages = Math.ceil(filteredCompanies.length / rowsPerPage);
                                                            const pages = [];
                                                            const showPages = 5;
                                                            let startPage = Math.max(1, currentPage - 2);
                                                            let endPage = Math.min(totalPages, startPage + showPages - 1);

                                                            if (endPage - startPage < showPages - 1) {
                                                                startPage = Math.max(1, endPage - showPages + 1);
                                                            }

                                                            if (startPage > 1) {
                                                                pages.push(
                                                                    <li key={1} className="page-item">
                                                                        <button
                                                                            className="page-link text-muted"
                                                                            style={{
                                                                                backgroundColor: "#f8f9fa",
                                                                                border: "1px solid #dee2e6",
                                                                                padding: "8px 12px",
                                                                                fontSize: "14px",
                                                                                minWidth: "40px",
                                                                                borderRadius: "6px"
                                                                            }}
                                                                            onClick={() => setCurrentPage(1)}
                                                                        >
                                                                            1
                                                                        </button>
                                                                    </li>
                                                                );
                                                                if (startPage > 2) {
                                                                    pages.push(
                                                                        <li key="ellipsis1" className="page-item disabled">
                                                                            <span className="page-link border-0 text-muted" style={{ backgroundColor: "transparent", padding: "4px 8px" }}>
                                                                                ...
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                }
                                                            }

                                                            for (let i = startPage; i <= endPage; i++) {
                                                                pages.push(
                                                                    <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
                                                                        <button
                                                                            className="page-link text-muted"
                                                                            style={{
                                                                                backgroundColor: currentPage === i ? "#F4F9FF" : "transparent",
                                                                                border: "1px solid #dee2e6",
                                                                                padding: "8px 12px",
                                                                                fontSize: "14px",
                                                                                minWidth: "40px",
                                                                                borderRadius: "6px",
                                                                                outline: "none",
                                                                                boxShadow: "none"
                                                                            }}
                                                                            onClick={() => setCurrentPage(i)}
                                                                        >
                                                                            {i}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            }

                                                            if (endPage < totalPages) {
                                                                if (endPage < totalPages - 1) {
                                                                    pages.push(
                                                                        <li key="ellipsis2" className="page-item disabled">
                                                                            <span className="page-link border-0 text-muted" style={{ backgroundColor: "transparent", padding: "4px 8px" }}>
                                                                                ...
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                }
                                                                pages.push(
                                                                    <li key={totalPages} className="page-item">
                                                                        <button
                                                                            className="page-link text-muted"
                                                                            style={{
                                                                                backgroundColor: "#f8f9fa",
                                                                                border: "1px solid #dee2e6",
                                                                                padding: "8px 12px",
                                                                                fontSize: "14px",
                                                                                minWidth: "40px",
                                                                                borderRadius: "6px"
                                                                            }}
                                                                            onClick={() => setCurrentPage(totalPages)}
                                                                        >
                                                                            {totalPages}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            }

                                                            return pages;
                                                        })()}

                                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                            <button
                                                                className="page-link text-muted"
                                                                style={{
                                                                    backgroundColor: "#f8f9fa",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "8px 12px",
                                                                    fontSize: "14px",
                                                                    borderRadius: "6px"
                                                                }}
                                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                disabled={currentPage === totalPages}
                                                            >
                                                                ›
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {isAddCompanyModalOpen && (
                    <AddCompanyAdminModal
                        isOpen={isAddCompanyModalOpen}
                        onClose={() => { setIsAddCompanyModalOpen(false) }}
                        onCompanyAdded={({ companyId, username, password }) => {
                            setLoginDetails(prev => ({
                                ...prev,
                                [companyId]: { username, password }
                            }));
                            setNewCompanyCredentials({ username, password });  // 🔑 Set creds
                            setShowCredentialsModal(true);
                            handleCompanyAdded(); // refresh list
                        }}
                    />
                )}
                {isDeleteCompanyModalOpen && (
                    <DeleteCompanyAdminModal
                        isOpen={isDeleteCompanyModalOpen}
                        onClose={() => { setIsDeleteCompanyModalOpen(false) }}
                        onCompanyDeleted={fetchCompanyAdmins}
                        companyAdminData={selectedCompany}
                    />
                )}
                {isEditCompanyModalOpen && (
                    <EditCompanyAdminModal
                        isOpen={isEditCompanyModalOpen}
                        onClose={() => { setIsEditCompanyModalOpen(false) }}
                        onCompanyEdited={fetchCompanyAdmins}
                        companyAdminData={selectedCompany}
                    />
                )}
                {isViewCompanyModalOpen && (
                    <ViewCompanyAdminModal
                        isOpen={isViewCompanyModalOpen}
                        onClose={() => { setIsViewCompanyModalOpen(false) }}
                        companyAdminData={selectedCompany}
                        onBrandingUpdated={(updated) => {
                            setSelectedCompany(updated);
                            fetchCompanyAdmins();
                        }}
                    />
                )}
                {showCredentialsModal && newCompanyCredentials && (
                    <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Company Created Successfully</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowCredentialsModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>Username:</strong> {newCompanyCredentials.username}</p>
                                    <p><strong>Password:</strong> {newCompanyCredentials.password}</p>
                                    <p><strong>Company Login Link:</strong> {app_link}/auth/{newCompanyCredentials.username}</p>
                                    <p className="text-muted">Please note down these credentials now. For your security, they won't be shown again.</p>
                                    <button
                                        className="btn btn-light"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`Username: ${newCompanyCredentials.username}\nPassword: ${newCompanyCredentials.password}\nLink: ${app_link}/auth/${newCompanyCredentials.username}`);
                                            toast.success("Copied to clipboard");
                                        }}
                                    >
                                        Copy to Clipboard
                                    </button>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCredentialsModal(false)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    style={{ top: "5rem", zIndex: 99999 }}
                />
            </div>
        </div>

    )
}

export { CompanyAdminList }
